import React, { useState, useRef, useEffect } from "react";
import { db } from "../utils/firebase";
import {
  collection,
  doc,
  updateDoc,
  getDocs,
  deleteDoc,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import { useAuth } from "../hooks/AuthenticationProvider";
import SignaturePad from "react-signature-canvas";
import {
  FormInput,
  Type,
  ListChecks,
  Edit3,
  Menu,
  GripVertical,
  Trash2,
  FileEdit,
  InfoIcon,
  Plus,
  X,
  Image,
  Calendar,
  CheckSquare,
  Settings2,
  BarChart3,
  AlertTriangle,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export const INTAKE_KEYS = {
  fullName: "Full Name",
  dob: "Date of Birth",
  phoneNumber: "Phone Number",
  email: "Email",
  intakeDate: "Intake Date",
  gender: "Gender",
  veteranStatus: "Veteran Status",
  workStatus: "Work Status",
  workOnEntry: "Work on Entry",
  ecFirstName: "E-Contact First Name",
  ecLastName: "E-Contact Last Name",
  ecRelationship: "E-Contact Relationship",
  ecPhoneNumber: "E-Contact Phone",
  ecEmail: "E-Contact Email",
  poFirstName: "PO First Name",
  poLastName: "PO Last Name",
  poEmail: "PO Email",
  county: "County",
  referralLocation: "Referral Location",
  drugOfChoice: "Drug of Choice",
  sobrietyDate: "Sobriety Date",
  isMAT: "Is on MAT?",
  medications: "Medications",
  pendingCharges: "Pending Charges",
};

const ANALYTICS_FIELDS = {
  isMAT: {
    requiredType: "yesNo",
    options: ["True", "False"],
    displayName: "MAT Status",
    description: "Required for addiction treatment analytics",
    gradient: "from-emerald-500 to-teal-600",
  },
  drugOfChoice: {
    requiredType: "multipleChoice",
    options: [
      "THC",
      "Alcohol",
      "Fentanyl",
      "Cocaine",
      "Crack",
      "Meth",
      "Amphetamines",
      "Cough Syrup",
    ],
    maxSelections: 2,
    displayName: "Drug of Choice",
    description: "Required for substance abuse analytics - max 2 selections",
    gradient: "from-red-500 to-pink-600",
  },
  workStatus: {
    requiredType: "dropdown",
    options: ["Unemployed", "Part-Time", "Full-Time", "Retired"],
    displayName: "Work Status",
    description: "Required for employment analytics",
    gradient: "from-blue-500 to-indigo-600",
  },
  workOnEntry: {
    requiredType: "dropdown",
    options: ["Unemployed", "Part-Time", "Full-Time", "Retired"],
    displayName: "Work Status on Entry",
    description: "Required for employment analytics",
    gradient: "from-blue-500 to-indigo-600",
  },
  veteranStatus: {
    requiredType: "dropdown",
    options: ["Veteran", "No military background"],
    displayName: "Veteran Status",
    description: "Required for veteran analytics",
    gradient: "from-purple-500 to-violet-600",
  },
  gender: {
    requiredType: "dropdown",
    options: ["Male", "Female", "Trans", "Other"],
    displayName: "Gender",
    description: "Required for demographic analytics",
    gradient: "from-orange-500 to-amber-600",
  },
};

const isAnalyticsField = (fieldKey) => {
  return ANALYTICS_FIELDS.hasOwnProperty(fieldKey);
};

const MobileElementMenu = ({ elements, onAddElement }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="fixed bottom-4 right-4 z-50 md:hidden">
      {/* Element selection menu */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 bg-slate-900/95 backdrop-blur-xl border border-slate-700 rounded-lg p-3 shadow-2xl w-64 mb-4 overflow-y-auto max-h-[70vh]">
          <div className="mb-2 pb-2 border-b border-slate-700 flex items-center justify-between">
            <h3 className="text-white font-medium text-sm">Add Element</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-slate-800 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
          <div className="space-y-2">
            {elements.map((element) => (
              <button
                key={element.id}
                onClick={() => {
                  onAddElement(element.id);
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-3 p-2 bg-slate-800/50 hover:bg-slate-800 rounded-lg text-white transition-colors text-sm"
              >
                <element.icon className="w-4 h-4" />
                <span>{element.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main FAB button with subtle animation */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-all duration-300 
          ${
            isOpen
              ? "bg-red-500 hover:bg-red-600 rotate-45"
              : "bg-blue-500 hover:bg-blue-600"
          } 
          animate-in fade-in slide-in-from-bottom-4`}
      >
        <Plus className="w-5 h-5 text-white" />
      </button>
    </div>
  );
};

const FloatingElementMenu = ({ elements, onAddElement }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const previousScrollY = useRef(0);

  // Add scroll listener to show/hide the FAB
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show the button when scrolling down past 300px
      if (currentScrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
        // Also close the menu if we scroll back to top
        if (isOpen) setIsOpen(false);
      }

      previousScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isOpen]);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-24 right-8 z-50 hidden md:block">
      {/* Element selection menu */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 bg-slate-900/95 backdrop-blur-xl border border-slate-700 rounded-lg p-4 shadow-2xl w-64 mb-4 overflow-y-auto max-h-[70vh]">
          <div className="mb-2 pb-2 border-b border-slate-700 flex items-center justify-between">
            <h3 className="text-white font-medium">Add Element</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-slate-800 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
          <div className="space-y-2">
            {elements.map((element) => (
              <button
                key={element.id}
                onClick={() => {
                  onAddElement(element.id);
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-3 p-3 bg-slate-800/50 hover:bg-slate-800 rounded-lg text-white transition-colors"
              >
                <element.icon className="w-5 h-5" />
                <span>{element.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main FAB button with subtle animation */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-all duration-300 
          ${
            isOpen
              ? "bg-red-500 hover:bg-red-600 rotate-45 scale-110"
              : "bg-blue-500 hover:bg-blue-600"
          } 
          animate-in fade-in slide-in-from-bottom-4`}
      >
        <Plus className="w-5 h-5 text-white" />
      </button>
    </div>
  );
};

const FORM_ELEMENTS = [
  {
    id: "headerImage",
    icon: Image, // Import this at the top
    label: "Header Image",
    hideLabel: true, // We don't need a label for a header image
  },
  {
    id: "applicantName",
    icon: FormInput, // Use the same icon as regular input or choose another
    label: "Name",
    type: "text",
    isApplicantIdentifier: true, // Special flag to identify this field
  },
  {
    id: "textBox",
    icon: Type, // We're reusing the Type icon, but you can change this
    label: "Text Box",
    hideLabel: true, // Add this flag to hide the label section
  },
  { id: "input", icon: FormInput, label: "Input Field", type: "text" },
  {
    id: "dropdown",
    icon: Type,
    label: "Drop Down Box",
    options: ["Option 1", "Option 2"],
  },
  {
    id: "phone",
    icon: FormInput,
    label: "Phone Number",
    type: "tel",
  },
  {
    id: "multipleChoice",
    icon: ListChecks,
    label: "Multiple Choice",
    options: ["Choice 1", "Choice 2"],
  },
  { id: "signature", icon: Edit3, label: "Signature Field" },
  { id: "date", icon: Calendar, label: "Date Field" },
  { id: "checkbox", icon: CheckSquare, label: "Checkbox" },
  {
    id: "yesNo",
    icon: CheckSquare,
    label: "Yes/No Button",
    options: ["Yes", "No"],
  },
];

const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, "");
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  }
  return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

const SortableFormElement = ({ element, onRemove, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [elementConfig, setElementConfig] = useState(element);
  const [newOption, setNewOption] = useState("");
  const signaturePadRef = useRef(null);

  // This is from @dnd-kit/sortable:
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: element.uniqueId,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleAnalyticsFieldMapping = (fieldKey, updatedConfig) => {
    if (!isAnalyticsField(fieldKey)) return updatedConfig;

    const analyticsField = ANALYTICS_FIELDS[fieldKey];

    // Transform the element to match analytics requirements
    const transformedConfig = {
      ...updatedConfig,
      id: analyticsField.requiredType,
      options: analyticsField.options,
      isAnalyticsField: true,
      analyticsFieldKey: fieldKey,
      analyticsFieldInfo: analyticsField,
    };

    // Special handling for multiple choice max selections
    if (fieldKey === "drugOfChoice") {
      transformedConfig.maxSelections = analyticsField.maxSelections;
    }

    return transformedConfig;
  };

  // Update the label whenever the user types in it
  const handleLabelChange = (e) => {
    const updatedConfig = { ...elementConfig, label: e.target.value };
    setElementConfig(updatedConfig);
    onUpdate(element.uniqueId, updatedConfig);
  };

  // Add new dropdown/multiple choice option
  const handleAddOption = () => {
    if (!newOption.trim()) return;

    const updatedOptions = [...(elementConfig.options || []), newOption.trim()];
    const updatedConfig = { ...elementConfig, options: updatedOptions };
    setElementConfig(updatedConfig);
    onUpdate(element.uniqueId, updatedConfig);
    setNewOption("");
  };

  // Remove a single option from dropdown/multiple choice
  const handleRemoveOption = (index) => {
    const updatedOptions = elementConfig.options.filter((_, i) => i !== index);
    const updatedConfig = { ...elementConfig, options: updatedOptions };
    setElementConfig(updatedConfig);
    onUpdate(element.uniqueId, updatedConfig);
  };

  // --------------------------------------
  // Configuration Panel (when isEditing = true)
  // --------------------------------------
  const renderElementConfig = () => {
    switch (element.id) {
      case "headerImage":
        return (
          <div className="space-y-2 mt-4">
            {elementConfig.imageUrl && (
              <div className="mb-4">
                <p className="text-slate-300 text-sm mb-2">
                  Current image: {elementConfig.fileName || "Header image"}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      document
                        .getElementById(`file-upload-${element.uniqueId}`)
                        .click();
                    }}
                    className="px-3 py-1 text-sm bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-md transition-colors"
                  >
                    Change Image
                  </button>
                  <button
                    onClick={() => {
                      const updatedConfig = {
                        ...elementConfig,
                        imageUrl: null,
                        fileName: null,
                      };
                      setElementConfig(updatedConfig);
                      onUpdate(element.uniqueId, updatedConfig);
                    }}
                    className="px-3 py-1 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-md transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-white text-sm mb-2 block">
                  Image Size
                </label>
                <select
                  value={elementConfig.size || "medium"}
                  onChange={(e) => {
                    const updatedConfig = {
                      ...elementConfig,
                      size: e.target.value,
                    };
                    setElementConfig(updatedConfig);
                    onUpdate(element.uniqueId, updatedConfig);
                  }}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              {elementConfig.size === "custom" && (
                <div>
                  <label className="text-white text-sm mb-2 block">
                    Custom Height (px)
                  </label>
                  <input
                    type="number"
                    min="50"
                    max="500"
                    value={elementConfig.customHeight || 150}
                    onChange={(e) => {
                      const updatedConfig = {
                        ...elementConfig,
                        customHeight: parseInt(e.target.value, 10),
                      };
                      setElementConfig(updatedConfig);
                      onUpdate(element.uniqueId, updatedConfig);
                    }}
                    className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                  />
                </div>
              )}

              <div>
                <label className="text-white text-sm mb-2 block">
                  Alignment
                </label>
                <select
                  value={elementConfig.alignment || "center"}
                  onChange={(e) => {
                    const updatedConfig = {
                      ...elementConfig,
                      alignment: e.target.value,
                    };
                    setElementConfig(updatedConfig);
                    onUpdate(element.uniqueId, updatedConfig);
                  }}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>
            </div>

            <input
              type="file"
              id={`file-upload-${element.uniqueId}`}
              className="hidden"
              accept="image/*"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  const file = e.target.files[0];

                  // Basic file validation
                  if (file.size > 5 * 1024 * 1024) {
                    alert("File size must be less than 5MB");
                    return;
                  }

                  const reader = new FileReader();
                  reader.onload = (event) => {
                    const updatedConfig = {
                      ...elementConfig,
                      imageUrl: event.target.result,
                      fileName: file.name,
                    };
                    setElementConfig(updatedConfig);
                    onUpdate(element.uniqueId, updatedConfig);
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
          </div>
        );
      case "textBox":
        return (
          <div className="space-y-4 mt-4">
            {/* Text area for content with styling applied */}
            <textarea
              value={elementConfig.content || ""}
              onChange={(e) => {
                const updatedConfig = {
                  ...elementConfig,
                  content: e.target.value,
                };
                setElementConfig(updatedConfig);
                onUpdate(element.uniqueId, updatedConfig);
              }}
              className="w-full h-32 px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-slate-400 transition-colors resize-none"
              placeholder="Enter text content here..."
              style={{
                fontWeight: elementConfig.isBold ? "bold" : "normal",
                fontStyle: elementConfig.isItalic ? "italic" : "normal",
                textDecoration: elementConfig.isUnderline
                  ? "underline"
                  : "none",
                textAlign: elementConfig.textAlign || "left",
                fontSize:
                  elementConfig.fontSize === "small"
                    ? "0.875rem"
                    : elementConfig.fontSize === "large"
                    ? "1.25rem"
                    : "1rem",
              }}
            />

            {/* Text formatting toolbar */}
            <div className="flex flex-wrap items-center gap-2 p-2 bg-slate-800 rounded-lg">
              <button
                onClick={() => {
                  const updatedConfig = {
                    ...elementConfig,
                    content: elementConfig.content || "",
                    isBold: !elementConfig.isBold,
                  };
                  setElementConfig(updatedConfig);
                  onUpdate(element.uniqueId, updatedConfig);
                }}
                className={`p-1.5 rounded-md ${
                  elementConfig.isBold
                    ? "bg-blue-500/30 text-blue-300"
                    : "hover:bg-slate-700 text-slate-300"
                }`}
                title="Bold"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
                  <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
                </svg>
              </button>

              <button
                onClick={() => {
                  const updatedConfig = {
                    ...elementConfig,
                    content: elementConfig.content || "",
                    isItalic: !elementConfig.isItalic,
                  };
                  setElementConfig(updatedConfig);
                  onUpdate(element.uniqueId, updatedConfig);
                }}
                className={`p-1.5 rounded-md ${
                  elementConfig.isItalic
                    ? "bg-blue-500/30 text-blue-300"
                    : "hover:bg-slate-700 text-slate-300"
                }`}
                title="Italic"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="19" y1="4" x2="10" y2="4"></line>
                  <line x1="14" y1="20" x2="5" y2="20"></line>
                  <line x1="15" y1="4" x2="9" y2="20"></line>
                </svg>
              </button>

              <button
                onClick={() => {
                  const updatedConfig = {
                    ...elementConfig,
                    content: elementConfig.content || "",
                    isUnderline: !elementConfig.isUnderline,
                  };
                  setElementConfig(updatedConfig);
                  onUpdate(element.uniqueId, updatedConfig);
                }}
                className={`p-1.5 rounded-md ${
                  elementConfig.isUnderline
                    ? "bg-blue-500/30 text-blue-300"
                    : "hover:bg-slate-700 text-slate-300"
                }`}
                title="Underline"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3"></path>
                  <line x1="4" y1="21" x2="20" y2="21"></line>
                </svg>
              </button>

              <div className="h-6 w-px bg-slate-700 mx-1"></div>

              <button
                onClick={() => {
                  const updatedConfig = {
                    ...elementConfig,
                    textAlign: "left",
                  };
                  setElementConfig(updatedConfig);
                  onUpdate(element.uniqueId, updatedConfig);
                }}
                className={`p-1.5 rounded-md ${
                  elementConfig.textAlign === "left" || !elementConfig.textAlign
                    ? "bg-blue-500/30 text-blue-300"
                    : "hover:bg-slate-700 text-slate-300"
                }`}
                title="Align Left"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="17" y1="10" x2="3" y2="10"></line>
                  <line x1="21" y1="6" x2="3" y2="6"></line>
                  <line x1="21" y1="14" x2="3" y2="14"></line>
                  <line x1="17" y1="18" x2="3" y2="18"></line>
                </svg>
              </button>

              <button
                onClick={() => {
                  const updatedConfig = {
                    ...elementConfig,
                    textAlign: "center",
                  };
                  setElementConfig(updatedConfig);
                  onUpdate(element.uniqueId, updatedConfig);
                }}
                className={`p-1.5 rounded-md ${
                  elementConfig.textAlign === "center"
                    ? "bg-blue-500/30 text-blue-300"
                    : "hover:bg-slate-700 text-slate-300"
                }`}
                title="Align Center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="10" x2="6" y2="10"></line>
                  <line x1="21" y1="6" x2="3" y2="6"></line>
                  <line x1="21" y1="14" x2="3" y2="14"></line>
                  <line x1="18" y1="18" x2="6" y2="18"></line>
                </svg>
              </button>

              <button
                onClick={() => {
                  const updatedConfig = {
                    ...elementConfig,
                    textAlign: "right",
                  };
                  setElementConfig(updatedConfig);
                  onUpdate(element.uniqueId, updatedConfig);
                }}
                className={`p-1.5 rounded-md ${
                  elementConfig.textAlign === "right"
                    ? "bg-blue-500/30 text-blue-300"
                    : "hover:bg-slate-700 text-slate-300"
                }`}
                title="Align Right"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="21" y1="10" x2="7" y2="10"></line>
                  <line x1="21" y1="6" x2="3" y2="6"></line>
                  <line x1="21" y1="14" x2="3" y2="14"></line>
                  <line x1="21" y1="18" x2="7" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Font size selection */}
            <div className="flex items-center gap-2">
              <label className="text-white text-sm">Font Size:</label>
              <select
                value={elementConfig.fontSize || "medium"}
                onChange={(e) => {
                  const updatedConfig = {
                    ...elementConfig,
                    fontSize: e.target.value,
                  };
                  setElementConfig(updatedConfig);
                  onUpdate(element.uniqueId, updatedConfig);
                }}
                className="px-2 py-1 bg-slate-800/50 border border-slate-700 rounded-md text-white"
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>

            {/* Required Field Checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={elementConfig.required || false}
                onChange={(e) => {
                  const updatedConfig = {
                    ...elementConfig,
                    required: e.target.checked,
                  };
                  setElementConfig(updatedConfig);
                  onUpdate(element.uniqueId, updatedConfig);
                }}
                className="cursor-pointer"
              />
              <label className="text-white text-sm">Required Field?</label>
            </div>
            {/* Map to Intake field */}
            <div className="mt-3">
              <label className="text-white text-sm mb-1 block">
                Map to Intake field
              </label>
              <select
                value={elementConfig.mapToIntake || ""}
                onChange={(e) => {
                  const fieldKey = e.target.value || null;
                  let updated = {
                    ...elementConfig,
                    mapToIntake: fieldKey,
                  };

                  // Check if this is an analytics field and transform if needed
                  if (fieldKey && isAnalyticsField(fieldKey)) {
                    updated = handleAnalyticsFieldMapping(fieldKey, updated);
                  } else {
                    // Remove analytics field properties if unmapping
                    delete updated.isAnalyticsField;
                    delete updated.analyticsFieldKey;
                    delete updated.analyticsFieldInfo;
                  }

                  setElementConfig(updated);
                  onUpdate(element.uniqueId, updated);
                }}
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
              >
                <option value="">— don’t import —</option>
                {Object.keys(INTAKE_KEYS).map((k) => (
                  <option key={k} value={k}>
                    {INTAKE_KEYS[k]}
                  </option>
                ))}
              </select>
              {elementConfig.isAnalyticsField &&
                elementConfig.id !==
                  elementConfig.analyticsFieldInfo.requiredType && (
                  <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="text-amber-200 text-sm">
                      <p className="font-medium mb-1">Analytics Warning</p>
                      <p>
                        This field is mapped to{" "}
                        <strong>
                          {elementConfig.analyticsFieldInfo.displayName}
                        </strong>{" "}
                        but uses a different element type. Analytics data may
                        not track properly unless you use the required{" "}
                        <strong>
                          {elementConfig.analyticsFieldInfo.requiredType}
                        </strong>{" "}
                        element type.
                      </p>
                    </div>
                  </div>
                )}
            </div>
          </div>
        );
      case "dropdown":
      case "multipleChoice":
        return (
          <div className="space-y-2 mt-4">
            {/* Options list */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                className="flex-1 px-3 py-1 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-sm"
                placeholder="Add new option"
              />
              <button
                onClick={handleAddOption}
                className="p-1.5 bg-blue-500/10 hover:bg-blue-500/20 rounded-lg"
              >
                <Plus className="w-4 h-4 text-blue-400" />
              </button>
            </div>

            <div className="space-y-2">
              {elementConfig.options?.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => {
                      const updatedOptions = [...elementConfig.options];
                      updatedOptions[index] = e.target.value;
                      const updatedConfig = {
                        ...elementConfig,
                        options: updatedOptions,
                      };
                      setElementConfig(updatedConfig);
                      onUpdate(element.uniqueId, updatedConfig);
                    }}
                    className="flex-1 px-3 py-1 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-sm"
                  />
                  <button
                    onClick={() => handleRemoveOption(index)}
                    className="p-1.5 hover:bg-red-500/20 rounded-lg"
                  >
                    <X className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              ))}
            </div>

            {/* Required Field Checkbox */}
            <div className="mt-4 flex items-center gap-2">
              <input
                type="checkbox"
                checked={elementConfig.required || false}
                onChange={(e) => {
                  const updatedConfig = {
                    ...elementConfig,
                    required: e.target.checked,
                  };
                  setElementConfig(updatedConfig);
                  onUpdate(element.uniqueId, updatedConfig);
                }}
                className="cursor-pointer"
              />
              <label className="text-white text-sm">Required Field?</label>
            </div>
            {/* Map to Intake field */}
            <div className="mt-3">
              <label className="text-white text-sm mb-1 block">
                Map to Intake field
              </label>
              <select
                value={elementConfig.mapToIntake || ""}
                onChange={(e) => {
                  const fieldKey = e.target.value || null;
                  let updated = {
                    ...elementConfig,
                    mapToIntake: fieldKey,
                  };

                  // Check if this is an analytics field and transform if needed
                  if (fieldKey && isAnalyticsField(fieldKey)) {
                    updated = handleAnalyticsFieldMapping(fieldKey, updated);
                  } else {
                    // Remove analytics field properties if unmapping
                    delete updated.isAnalyticsField;
                    delete updated.analyticsFieldKey;
                    delete updated.analyticsFieldInfo;
                  }

                  setElementConfig(updated);
                  onUpdate(element.uniqueId, updated);
                }}
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
              >
                <option value="">— don’t import —</option>
                {Object.keys(INTAKE_KEYS).map((k) => (
                  <option key={k} value={k}>
                    {INTAKE_KEYS[k]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      default:
        return (
          <div className="mt-4">
            {/* If you have any special config for this field type, show it here.
                For example, a phone field or textBox content, etc. 
                But we add the required checkbox for all. */}

            {/* Required Field Checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={elementConfig.required || false}
                onChange={(e) => {
                  const updatedConfig = {
                    ...elementConfig,
                    required: e.target.checked,
                  };
                  setElementConfig(updatedConfig);
                  onUpdate(element.uniqueId, updatedConfig);
                }}
                className="cursor-pointer"
              />
              <label className="text-white text-sm">Required Field?</label>
            </div>
            {/* Map to Intake field */}
            <div className="mt-3">
              <label className="text-white text-sm mb-1 block">
                Map to Intake field
              </label>
              <select
                value={elementConfig.mapToIntake || ""}
                onChange={(e) => {
                  const fieldKey = e.target.value || null;
                  let updated = {
                    ...elementConfig,
                    mapToIntake: fieldKey,
                  };

                  // Check if this is an analytics field and transform if needed
                  if (fieldKey && isAnalyticsField(fieldKey)) {
                    updated = handleAnalyticsFieldMapping(fieldKey, updated);
                  } else {
                    // Remove analytics field properties if unmapping
                    delete updated.isAnalyticsField;
                    delete updated.analyticsFieldKey;
                    delete updated.analyticsFieldInfo;
                  }

                  setElementConfig(updated);
                  onUpdate(element.uniqueId, updated);
                }}
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
              >
                <option value="">— don’t import —</option>
                {Object.keys(INTAKE_KEYS).map((k) => (
                  <option key={k} value={k}>
                    {INTAKE_KEYS[k]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
    }
  };

  // --------------------------------------
  // Preview Render for each element
  // --------------------------------------
  const renderPreview = () => {
    switch (element.id) {
      case "headerImage":
        let imageHeight = "12rem"; // Default/medium
        if (elementConfig.size === "small") imageHeight = "8rem";
        if (elementConfig.size === "large") imageHeight = "16rem";
        if (elementConfig.size === "custom")
          imageHeight = `${elementConfig.customHeight || 150}px`;

        let alignmentClass = "mx-auto"; // center default
        if (elementConfig.alignment === "left") alignmentClass = "ml-0 mr-auto";
        if (elementConfig.alignment === "right")
          alignmentClass = "mr-0 ml-auto";

        return (
          <div className="relative z-50 w-full">
            {elementConfig.imageUrl ? (
              <div className="relative">
                <img
                  src={elementConfig.imageUrl}
                  alt="Header"
                  className={`max-w-full ${alignmentClass} rounded-lg object-contain`}
                  style={{ maxHeight: imageHeight }}
                />
                {isEditing && (
                  <button
                    onClick={() => {
                      const updatedConfig = {
                        ...elementConfig,
                        imageUrl: null,
                      };
                      setElementConfig(updatedConfig);
                      onUpdate(element.uniqueId, updatedConfig);
                    }}
                    className="absolute top-2 right-2 p-2 bg-red-500/70 hover:bg-red-500/90 rounded-full text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 bg-slate-800/50 border border-dashed border-slate-700 rounded-lg p-4">
                <Image className="w-10 h-10 text-slate-500 mb-2" />
                <p className="text-slate-400 mb-3">Upload a header image</p>
                <input
                  type="file"
                  id={`file-upload-${element.uniqueId}`}
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      const file = e.target.files[0];

                      // Basic file size validation (5MB limit)
                      if (file.size > 5 * 1024 * 1024) {
                        alert("File size must be less than 5MB");
                        return;
                      }

                      const reader = new FileReader();
                      reader.onload = (event) => {
                        const updatedConfig = {
                          ...elementConfig,
                          imageUrl: event.target.result,
                          fileName: file.name,
                          size: elementConfig.size || "medium",
                          alignment: elementConfig.alignment || "center",
                        };
                        setElementConfig(updatedConfig);
                        onUpdate(element.uniqueId, updatedConfig);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                <label
                  htmlFor={`file-upload-${element.uniqueId}`}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg cursor-pointer transition-colors"
                >
                  Choose Image
                </label>
              </div>
            )}
          </div>
        );
      case "applicantName":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                className="relative z-50 w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-slate-400 transition-colors"
                placeholder="First Name"
                disabled
              />
              <input
                type="text"
                className="relative z-50 w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-slate-400 transition-colors"
                placeholder="Last Name"
                disabled
              />
            </div>
          </div>
        );
      case "input":
        return (
          <input
            type="text"
            className="relative z-50 w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-slate-400 transition-colors"
            placeholder="Text input"
            disabled
          />
        );
      case "phone":
        return (
          <input
            type="tel"
            className="relative z-50 w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-slate-400 transition-colors"
            placeholder="XXX-XXX-XXXX"
            value={elementConfig.value || ""}
            onChange={(e) => {
              // This is not actively used in real form mode (since it's disabled),
              // but let's keep the logic for phone formatting.
              const formatted = formatPhoneNumber(e.target.value);
              const updatedConfig = {
                ...elementConfig,
                value: formatted,
              };
              setElementConfig(updatedConfig);
              onUpdate(element.uniqueId, updatedConfig);
            }}
            maxLength={12}
          />
        );
      case "dropdown":
        return (
          <select
            className="relative z-50 w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white transition-colors"
            disabled
          >
            {elementConfig.options?.map((option, index) => (
              <option key={index}>{option}</option>
            ))}
          </select>
        );
      case "textBox":
        // If editing, don't show anything here - it will be shown in renderElementConfig
        if (isEditing) {
          return null;
        }

        // Determine text styles based on formatting options
        const textStyles = {
          fontWeight: elementConfig.isBold ? "bold" : "normal",
          fontStyle: elementConfig.isItalic ? "italic" : "normal",
          textDecoration: elementConfig.isUnderline ? "underline" : "none",
          textAlign: elementConfig.textAlign || "left",
        };

        // Determine font size
        let fontSize = "1rem"; // medium (default)
        if (elementConfig.fontSize === "small") fontSize = "0.875rem";
        if (elementConfig.fontSize === "large") fontSize = "1.25rem";

        return (
          <textarea
            value={elementConfig.content || ""}
            onChange={(e) => {
              const updatedConfig = {
                ...elementConfig,
                content: e.target.value,
              };
              setElementConfig(updatedConfig);
              onUpdate(element.uniqueId, updatedConfig);
            }}
            className="relative z-50 w-full min-h-[8rem] px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-slate-400 transition-colors resize-none"
            placeholder="Text content will appear here..."
            style={{ fontSize, ...textStyles }}
          />
        );
      case "yesNo":
        return (
          <select
            className="relative z-50 w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white transition-colors"
            disabled
          >
            {elementConfig.options?.map((option, index) => (
              <option key={index}>{option}</option>
            )) || (
              <>
                <option>Yes</option>
                <option>No</option>
              </>
            )}
          </select>
        );
      case "multipleChoice":
        const maxSelections =
          elementConfig.maxSelections || elementConfig.options?.length || 999;
        const selectionType = maxSelections === 1 ? "radio" : "checkbox";

        return (
          <div className="space-y-2">
            {elementConfig.maxSelections && (
              <p className="text-xs text-slate-400 mb-2">
                Select up to {elementConfig.maxSelections} option
                {elementConfig.maxSelections > 1 ? "s" : ""}
              </p>
            )}
            {elementConfig.options?.map((option, index) => (
              <label key={index} className="flex items-center gap-2 text-white">
                <input
                  type={selectionType}
                  name={
                    selectionType === "radio"
                      ? `${element.uniqueId}-options`
                      : undefined
                  }
                  disabled
                  className="text-blue-500 focus:ring-blue-500"
                />
                {option}
              </label>
            ))}
          </div>
        );
      case "date":
        return (
          <input
            type="date"
            className="relative z-50 w-full px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:border-blue-500 text-white transition-colors"
            disabled
          />
        );
      case "checkbox":
        return (
          <label className="flex items-center gap-2 text-white">
            <input
              type="checkbox"
              disabled
              className="rounded text-blue-500 focus:ring-blue-500"
            />
            {elementConfig.label}
          </label>
        );
      case "signature":
        return (
          <div className="relative z-50">
            <div className="w-full bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
              <SignaturePad
                ref={signaturePadRef}
                canvasProps={{
                  className: "w-full h-32 bg-slate-900/90",
                }}
                dotSize={2}
                minWidth={2}
                maxWidth={3}
                penColor="white"
                onEnd={() => {
                  if (signaturePadRef.current) {
                    const dataUrl = signaturePadRef.current
                      .getTrimmedCanvas()
                      .toDataURL("image/png");
                    const updatedConfig = {
                      ...elementConfig,
                      signatureData: dataUrl,
                    };
                    setElementConfig(updatedConfig);
                    onUpdate(element.uniqueId, updatedConfig);
                  }
                }}
              />
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <button
                onClick={() => {
                  signaturePadRef.current?.clear();
                  const updatedConfig = {
                    ...elementConfig,
                    signatureData: null,
                  };
                  setElementConfig(updatedConfig);
                  onUpdate(element.uniqueId, updatedConfig);
                }}
                className="px-3 py-1 text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-md transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500 mb-4"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* If hideLabel is true, we don't show the usual label row, only the top-right controls */}
      {element.hideLabel && (
        <div className="absolute top-4 right-4 flex items-center gap-2 z-[100]">
          <button
            type="button"
            onClick={() => setIsEditing(!isEditing)}
            className="p-1 sm:p-1.5 hover:bg-blue-500/20 rounded-lg bg-slate-800/50"
          >
            <Settings2 className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
          </button>
          <button
            type="button"
            onClick={() => onRemove(element.uniqueId)}
            className="p-1.5 hover:bg-red-500/20 rounded-lg bg-slate-800/50"
          >
            <X className="w-4 h-4 text-red-400" />
          </button>
          <div
            {...attributes}
            {...listeners}
            className="cursor-move p-1.5 hover:bg-slate-700/50 rounded-lg bg-slate-800/50"
          >
            <GripVertical className="w-4 h-4 text-slate-400" />
          </div>
        </div>
      )}

      {/* If hideLabel is NOT true, show the label row */}
      {!element.hideLabel && (
        <div
          className="relative flex items-center justify-between
                p-4 border-b border-slate-800/50"
        >
          {/* LEFT side ─ icon + editable label */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <element.icon size={20} className="text-blue-400" />
            </div>

            {/* 2nd flex layer — let *this* stretch too */}
            <div className="flex-1 min-w-0 flex items-center">
              <input
                type="text"
                value={elementConfig.label}
                onChange={handleLabelChange}
                /*          ▼ the magic is flex-1 min-w-0  */
                className="flex-1 min-w-0 bg-transparent text-white px-2
             border-none focus:outline-none focus:border-b
             focus:border-blue-400"
                placeholder="Field Label"
              />
              {elementConfig.required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </div>
          </div>
          {elementConfig.isAnalyticsField && (
            <div className="mt-4 p-3 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-lg relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-cyan-500/5 animate-pulse"></div>
              <div className="relative flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-full animate-pulse">
                  <BarChart3 className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <span className="text-emerald-300 font-medium text-sm">
                    📊 Analytics-Enabled Field
                  </span>
                  <p className="text-emerald-200/80 text-xs mt-1">
                    {elementConfig.analyticsFieldInfo.description}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsEditing(!isEditing)}
              className="p-1.5 hover:bg-blue-500/20 rounded-lg"
            >
              <Settings2 className="w-4 h-4 text-blue-400" />
            </button>
            <button
              type="button"
              onClick={() => onRemove(element.uniqueId)}
              className="p-1.5 hover:bg-red-500/20 rounded-lg"
            >
              <X className="w-4 h-4 text-red-400" />
            </button>
            <div
              {...attributes}
              {...listeners}
              className="cursor-move p-1.5 hover:bg-slate-700/50 rounded-lg"
            >
              <GripVertical className="w-4 h-4 text-slate-400" />
            </div>
          </div>
        </div>
      )}

      {/* Preview of the element + Edit config if isEditing */}
      <div className="p-4 relative">
        {renderPreview()}
        {isEditing && renderElementConfig()}
      </div>
    </div>
  );
};

const Forms = () => {
  const [formName, setFormName] = useState("Untitled Form");
  const [formElements, setFormElements] = useState([]);
  const { organizationId } = useAuth();
  const [isSaving, setSaving] = useState(false);
  const [isManagingForms, setIsManagingForms] = useState(false);
  const [existingForms, setExistingForms] = useState([]);
  const [isApplication, setIsApplication] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [isResidentDocument, setIsResidentDocument] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  //Put these back in if you add the reoccuring forms back in
  // const [isReoccurringForm, setIsReoccurringForm] = useState(false);
  //const [reoccurrenceType, setReoccurrenceType] = useState("weekly");
  //const [reoccurrenceDay, setReoccurrenceDay] = useState("");
  //const [reoccurrenceDate, setReoccurrenceDate] = useState("");

  const fetchExistingForms = async () => {
    try {
      const formsCollectionRef = collection(
        db,
        `organizations/${organizationId}/forms`
      );
      const querySnapshot = await getDocs(formsCollectionRef);
      const formsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setExistingForms(formsData);
    } catch (error) {
      console.error("Error fetching forms:", error);
    }
  };

  const generateEmbedCode = (form) => {
    const shareUrl = `${window.location.origin}/public-forms/${organizationId}/${form.id}`;

    return `<iframe
    id="CustomFormIFrame-${form.id}"
    title="${form.name || "Form"}"
    allowtransparency="true"
    allowfullscreen="true"
    src="${shareUrl}?embed=true"
    frameborder="0"
    style="min-width:100%;max-width:100%;height:1500px;border:none;"
    scrolling="no"
  >
  </iframe>
  <script type="text/javascript">
  var ifr = document.getElementById("CustomFormIFrame-${form.id}");
  if (ifr) {
    var src = ifr.src;
    var iframeParams = [];
    if (window.location.href && window.location.href.indexOf("?") > -1) {
      iframeParams = iframeParams.concat(window.location.href.substr(window.location.href.indexOf("?") + 1).split('&'));
    }
    if (src && src.indexOf("?") > -1) {
      iframeParams = iframeParams.concat(src.substr(src.indexOf("?") + 1).split("&"));
      src = src.substr(0, src.indexOf("?"))
    }
    iframeParams.push("isIframeEmbed=1");
    ifr.src = src + "?" + iframeParams.join('&');
  }
  window.handleIFrameMessage = function(e) {
    if (typeof e.data === 'object') { 
      var iframe = document.getElementById("CustomFormIFrame-${form.id}");
      if (!iframe) return;
      
      switch (e.data.type) {
        case "setHeight":
          iframe.style.height = e.data.height + "px";
          break;
        case "formSubmitted":
          console.log("Form was submitted successfully");
          break;
      }
    }
  };
  if (window.addEventListener) {
    window.addEventListener("message", handleIFrameMessage, false);
  } else if (window.attachEvent) {
    window.attachEvent("onmessage", handleIFrameMessage);
  }
  </script>`;
  };

  const handleUpdateForm = async () => {
    if (!editingForm || !organizationId) {
      console.error("Missing editingForm or organizationId");
      return;
    }

    setSaving(true);
    try {
      const formRef = doc(
        db,
        `organizations/${organizationId}/forms`,
        editingForm.id
      );

      console.log("Form elements before cleaning:", formElements);

      const cleanedElements = formElements.map((element, index) => {
        console.log(`Processing element ${index}:`, element);

        // Check if element.id exists
        if (!element.id) {
          console.error("Element missing id property:", element);
          // If this is from an existing form, the property might be named 'type' instead of 'id'
          element.id = element.type || "input"; // Default to 'input' if both are missing
        }

        let baseElement = {
          type: element.id,
          label: element.label || "Untitled Field",
          options: element.options || [],
          value: element.value || "",
          uniqueId: element.uniqueId || `${element.id}-${Date.now()}`,
          order: index, // Add order index
        };

        switch (element.id || element.type) {
          case "applicantName":
            baseElement.valueType = "string";
            baseElement.defaultValue = "";
            baseElement.isApplicantIdentifier = true;
            break;

          case "yesNo":
            baseElement.valueType = "boolean";
            baseElement.options = ["Yes", "No"];
            baseElement.defaultValue = null;
            break;

          case "headerImage":
            baseElement.valueType = "image";
            baseElement.imageUrl = element.imageUrl || null;
            baseElement.fileName = element.fileName || null;
            baseElement.size = element.size || "medium";
            baseElement.alignment = element.alignment || "center";
            if (element.size === "custom") {
              baseElement.customHeight = element.customHeight || 150;
            }
            break;

          case "phone":
            baseElement.valueType = "string";
            baseElement.value = element.value || "";
            baseElement.defaultValue = "";
            baseElement.validation = {
              pattern: "^\\d{3}-\\d{3}-\\d{4}$",
              message: "Please enter a valid phone number (XXX-XXX-XXXX)",
            };
            break;

          case "textBox":
            baseElement.valueType = "string";
            baseElement.content = element.content || "";
            baseElement.defaultValue = element.content || "";

            // Add the rich text formatting properties
            baseElement.isBold = element.isBold || false;
            baseElement.isItalic = element.isItalic || false;
            baseElement.isUnderline = element.isUnderline || false;
            baseElement.textAlign = element.textAlign || "left";
            baseElement.fontSize = element.fontSize || "medium";
            break;

          case "dropdown":
            baseElement.valueType = "string";
            baseElement.options = element.options || [];
            baseElement.defaultValue = element.options?.[0] || null;
            break;

          case "multipleChoice":
            baseElement.valueType = "string";
            baseElement.options = element.options || [];
            baseElement.defaultValue = null;
            break;

          case "date":
            baseElement.valueType = "timestamp";
            baseElement.defaultValue = Timestamp.now();
            break;

          case "checkbox":
            baseElement.valueType = "boolean";
            baseElement.defaultValue = false;
            break;

          case "signature":
            baseElement.valueType = "image";
            baseElement.defaultValue = null;
            break;

          case "input":
          default:
            baseElement.valueType = "string";
            baseElement.defaultValue = "";
            break;
        }

        baseElement.required = element.required || false;
        baseElement.mapToIntake = element.mapToIntake || null;
        if (element.config) {
          baseElement.config = element.config;
        }

        console.log("Cleaned element:", baseElement);
        return baseElement;
      });

      // Create elementOrder array
      const elementOrder = cleanedElements.map((el) => el.uniqueId);
      console.log("Element order:", elementOrder);

      const updatedFormData = {
        name: formName,
        elements: cleanedElements,
        lastModified: Timestamp.now(),
        elementOrder: elementOrder,
        isApplication: isApplication,
        showOnDashboard: showDashboard,
        isResidentDocument: isResidentDocument, // Add this line
        // isReoccurringForm: isReoccurringForm, // Uncomment if needed
        // reoccurrenceType: reoccurrenceType, // Uncomment if needed
        // reoccurrenceDay: reoccurrenceDay, // Uncomment if needed
        // reoccurrenceDate: reoccurrenceDate, // Uncomment if needed
        embedCode: generateEmbedCode({
          id: editingForm.id,
          name: formName,
        }),
      };

      console.log("Full updated form data:", updatedFormData);

      await updateDoc(formRef, updatedFormData);
      alert("Form updated successfully!");
      setEditingForm(null);
      setFormElements([]);
      setFormName("Untitled Form");
      setIsApplication(false); // Reset the checkbox
      setIsManagingForms(false);
    } catch (error) {
      console.error("Error updating form:", error);
      alert("Error updating form. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  //function to handle selecting a form to edit
  const handleSelectForm = (form) => {
    setEditingForm(form);
    setFormName(form.name);
    setIsApplication(form.isApplication || false);
    setShowDashboard(form.showOnDashboard || false);
    setIsResidentDocument(form.isResidentDocument || false);

    const transformedElements = form.elements.map((element) => {
      const template = FORM_ELEMENTS.find((fe) => fe.id === element.type);

      return {
        ...element,
        id: element.type,
        uniqueId:
          element.uniqueId || `${element.type}-${Date.now()}-${Math.random()}`,
        options: element.options || [],
        value: element.value || "",
        icon: template?.icon || FORM_ELEMENTS[0].icon,
      };
    });

    setFormElements(transformedElements);
    setIsManagingForms(false);
  };

  const handleSaveForm = async () => {
    if (!organizationId) {
      console.error("No organization ID found");
      return;
    }
    setSaving(true);
    try {
      const cleanedElements = formElements.map((element, index) => {
        let baseElement = {
          type: element.id,
          label: element.label,
          options: element.options || [],
          value: element.value || "",
          uniqueId: element.uniqueId || `${element.id}-${Date.now()}`,
          order: index, // Add order index
        };

        switch (element.id) {
          case "yesNo":
            baseElement.valueType = "boolean";
            baseElement.options = ["Yes", "No"];
            baseElement.defaultValue = null;
            break;

          case "headerImage":
            baseElement.valueType = "image";
            baseElement.imageUrl = element.imageUrl || null;
            baseElement.fileName = element.fileName || null;
            baseElement.size = element.size || "medium";
            baseElement.alignment = element.alignment || "center";
            if (element.size === "custom") {
              baseElement.customHeight = element.customHeight || 150;
            }
            break;
          case "applicantName":
            baseElement.valueType = "string";
            baseElement.defaultValue = "";
            baseElement.isApplicantIdentifier = true;
            baseElement.firstName = element.firstName || "";
            baseElement.lastName = element.lastName || "";
            break;
          case "phone":
            baseElement.valueType = "string";
            baseElement.value = element.value || "";
            baseElement.defaultValue = "";
            baseElement.validation = {
              pattern: "^\\d{3}-\\d{3}-\\d{4}$",
              message: "Please enter a valid phone number (XXX-XXX-XXXX)",
            };
            break;

          case "textBox":
            baseElement.valueType = "string";
            baseElement.content = element.content || "";
            baseElement.defaultValue = element.content || "";

            // Add the rich text formatting properties
            baseElement.isBold = element.isBold || false;
            baseElement.isItalic = element.isItalic || false;
            baseElement.isUnderline = element.isUnderline || false;
            baseElement.textAlign = element.textAlign || "left";
            baseElement.fontSize = element.fontSize || "medium";
            break;

          case "dropdown":
            baseElement.valueType = "string";
            baseElement.options = element.options || [];
            baseElement.defaultValue = element.options?.[0] || null;
            break;

          case "multipleChoice":
            baseElement.valueType = "string";
            baseElement.options = element.options || [];
            baseElement.defaultValue = null;
            break;

          case "date":
            baseElement.valueType = "timestamp";
            baseElement.defaultValue = Timestamp.now();
            break;

          case "checkbox":
            baseElement.valueType = "boolean";
            baseElement.defaultValue = false;
            break;

          case "signature":
            baseElement.valueType = "image";
            baseElement.defaultValue = null;
            break;

          case "input":
          default:
            baseElement.valueType = "string";
            baseElement.defaultValue = "";
            break;
        }

        baseElement.required = element.required || false;
        baseElement.mapToIntake = element.mapToIntake || null;
        if (element.config) {
          baseElement.config = element.config;
        }
        return baseElement;
      });

      // Check if this is an application form and validate the applicant name field
      if (isApplication) {
        const applicantNameElements = cleanedElements.filter(
          (el) => el.type === "applicantName" || el.isApplicantIdentifier
        );

        if (applicantNameElements.length === 0) {
          if (
            !confirm(
              "Your application form doesn't have an Applicant Name field. This will make it hard to identify applicants. Continue anyway?"
            )
          ) {
            setSaving(false);
            return;
          }
        } else if (applicantNameElements.length > 1) {
          alert(
            "Your form has multiple Applicant Name fields. Only the first one will be used to identify applicants."
          );
        }
      }

      const elementOrder = cleanedElements.map((el) => el.uniqueId);

      const formData = {
        name: formName,
        elements: cleanedElements,
        dateCreated: Timestamp.now(),
        lastModified: Timestamp.now(),
        version: "1.0",
        elementOrder: elementOrder,
        isApplication: isApplication,
        isResidentDocument: isResidentDocument,
        // Add new fields
        showOnDashboard: showDashboard,
        /* comment this back in if you add back reoccuring forms through this page
        isReoccurringForm: isReoccurringForm,
        // Only include reoccurrence settings if it's a recurring form
        ...(isReoccurringForm && {
          reoccurrenceType: reoccurrenceType,
          reoccurrenceDay: reoccurrenceDay,
          reoccurrenceDate: reoccurrenceDate
            ? Timestamp.fromDate(new Date(reoccurrenceDate))
            : null,
        }),
        */
      };

      await addDoc(
        collection(db, `organizations/${organizationId}/forms`),
        formData
      );

      alert("Form saved successfully!");
      setFormElements([]);
      setFormName("Untitled Form");
      setIsApplication(false);
      setShowDashboard(false); // Make sure this is included
      setIsResidentDocument(false);
    } catch (error) {
      console.error("Error saving form:", error);
      alert("Error saving form. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: closestCenter,
    })
  );

  const handleDeleteForm = async (formId) => {
    if (!organizationId) return;

    if (window.confirm("Are you sure you want to delete this form?")) {
      try {
        const formRef = doc(
          db,
          `organizations/${organizationId}/forms`,
          formId
        );
        await deleteDoc(formRef);

        // Remove from local state
        setExistingForms(existingForms.filter((form) => form.id !== formId));
        alert("Form deleted successfully!");
      } catch (error) {
        console.error("Error deleting form:", error);
        alert("Error deleting form. Please try again.");
      }
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = formElements.findIndex(
        (elem) => elem.uniqueId === active.id
      );
      const newIndex = formElements.findIndex(
        (elem) => elem.uniqueId === over.id
      );
      const newElements = [...formElements];
      const [removed] = newElements.splice(oldIndex, 1);
      newElements.splice(newIndex, 0, removed);
      setFormElements(newElements);
    }
  };

  // Find this function in your Forms.jsx file
  const addFormElement = (elementType) => {
    if (elementType === "formTitle") {
      setIsTitleModalOpen(true);
      return;
    }

    const element = FORM_ELEMENTS.find((elem) => elem.id === elementType);
    if (element) {
      // Set required to false by default for headerImage and textBox
      const isRequired =
        element.id !== "headerImage" && element.id !== "textBox";

      const newElement = {
        ...element,
        uniqueId: `${element.id}-${Date.now()}`,
        required: isRequired,
      };

      setFormElements([...formElements, newElement]);
    }
  };

  const removeFormElement = (elementId) => {
    setFormElements(formElements.filter((elem) => elem.uniqueId !== elementId));
  };

  const updateFormElement = (elementId, updatedConfig) => {
    setFormElements(
      formElements.map((elem) =>
        elem.uniqueId === elementId ? { ...elem, ...updatedConfig } : elem
      )
    );
  };

  return (
    <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-2 sm:p-6 relative overflow-hidden">
      {/* Ambient background effects */}
      <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
      </div>

      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-6 mb-6 sm:mb-12">
          <div className="flex gap-1 sm:gap-3">
            <div className="h-10 sm:h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
            <div className="h-10 sm:h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
            <div className="h-10 sm:h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
          </div>
          <div className="flex-1">
            <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {/* Mobile Sidebar Toggle Button */}
                <button
                  className="md:hidden p-2 bg-slate-800/50 rounded-lg text-white"
                  onClick={() => setShowSidebar(!showSidebar)}
                >
                  <Menu className="w-5 h-5" />
                </button>
                <h1 className="text-2xl sm:text-4xl font-bold text-white tracking-tight">
                  Form Builder
                </h1>
              </div>
              <button
                onClick={() => {
                  fetchExistingForms();
                  setIsManagingForms(true);
                }}
                className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-slate-800/50 hover:bg-slate-800 text-white rounded-lg transition-colors text-sm sm:text-base w-full sm:w-auto justify-center sm:justify-start"
              >
                <FileEdit className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Manage Forms</span>
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-4 md:gap-8">
          {/* Mobile Sidebar (offscreen drawer) */}
          <div
            className={`fixed inset-0 bg-slate-950/90 backdrop-blur-sm z-50 md:hidden transition-transform duration-300 transform ${
              showSidebar ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <div className="h-full w-4/5 max-w-xs bg-slate-900 p-4">
              <div className="flex justify-between items-center mb-4 border-b border-slate-800 pb-4">
                <h2 className="text-lg font-semibold text-white">
                  Form Elements
                </h2>
                <button
                  className="p-2 hover:bg-slate-800 rounded-lg"
                  onClick={() => setShowSidebar(false)}
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <div className="overflow-y-auto space-y-2 max-h-[calc(100vh-100px)]">
                {FORM_ELEMENTS.map((element) => (
                  <button
                    key={element.id}
                    onClick={() => {
                      addFormElement(element.id);
                      setShowSidebar(false);
                    }}
                    className="w-full flex items-center gap-3 p-3 bg-slate-800/50 hover:bg-slate-800 rounded-lg text-white transition-colors"
                  >
                    <element.icon className="w-5 h-5" />
                    <span>{element.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Desktop Sidebar */}
          <div className="hidden md:block md:w-1/4">
            <div className="sticky top-6 group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
              <div className="p-4 border-b border-slate-800/50">
                <h2 className="text-lg font-semibold text-white">
                  Form Elements
                </h2>
              </div>
              <div className="max-h-[calc(100vh-150px)] overflow-y-auto p-4 space-y-2">
                {FORM_ELEMENTS.map((element) => (
                  <button
                    key={element.id}
                    onClick={() => addFormElement(element.id)}
                    className="w-full flex items-center gap-3 p-3 bg-slate-800/50 hover:bg-slate-800 rounded-lg text-white transition-colors"
                  >
                    <element.icon className="w-5 h-5" />
                    <span>{element.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Form Canvas */}
          <div className="flex-1">
            {/* Form Preview Container - always visible */}
            <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-800/50 p-3 sm:p-6">
              {/* Title Field - always visible */}
              <div className="w-full sm:w-1/2 mx-auto mb-4 sm:mb-8">
                <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
                  <div className="relative flex items-center justify-between p-3 sm:p-4 border-b border-slate-800/50">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="p-1.5 sm:p-2 bg-blue-500/10 rounded-lg">
                        <Edit3 className="text-blue-400" size={18} />
                      </div>
                      <input
                        type="text"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        className="relative z-50 bg-transparent text-white focus:outline-none focus:border-b border-blue-400 px-2 w-full text-sm sm:text-base"
                        placeholder="Form Title"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Elements */}
              <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={formElements}
                  strategy={verticalListSortingStrategy}
                >
                  {formElements.map((element) => (
                    <SortableFormElement
                      key={element.uniqueId}
                      element={element}
                      onRemove={removeFormElement}
                      onUpdate={updateFormElement}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              {formElements.length === 0 && (
                <div className="flex flex-col items-center justify-center h-40 sm:h-64 text-slate-400">
                  <Plus className="w-8 h-8 sm:w-12 sm:h-12 mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-center">
                    Add form elements from the sidebar
                  </p>
                </div>
              )}
            </div>
            {formElements.length > 0 && (
              <div className="mt-4 sm:mt-8 bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-800/50 p-3 sm:p-6 w-full">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">
                  Form Settings
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {/* Left column - basic options */}
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center gap-2 relative">
                      <input
                        type="checkbox"
                        id="isApplicationCheckbox"
                        checked={isApplication}
                        onChange={(e) => setIsApplication(e.target.checked)}
                        className="rounded text-blue-500 focus:ring-blue-500 h-4 w-4"
                      />
                      <label
                        htmlFor="isApplicationCheckbox"
                        className="text-slate-300 text-sm"
                      >
                        Is this your house application?
                      </label>

                      {/* Info Icon with Tooltip */}
                      <div className="relative group">
                        <InfoIcon className="w-4 h-4 text-slate-400 cursor-help" />
                        <div className="absolute left-0 sm:left-1/2 transform sm:transform-x-1/2 bottom-full mb-2 hidden group-hover:block bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-3 text-slate-300 w-52 sm:w-64 z-50">
                          Is this the form that potential residents fill out in
                          order to initially apply to your home?
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 relative">
                      <input
                        type="checkbox"
                        id="showDashboardCheckbox"
                        checked={showDashboard}
                        onChange={(e) => setShowDashboard(e.target.checked)}
                        className="rounded text-blue-500 focus:ring-blue-500 h-4 w-4"
                      />
                      <label
                        htmlFor="showDashboardCheckbox"
                        className="text-slate-300 text-sm"
                      >
                        Show on Resident Dashboard
                      </label>

                      {/* Wrap only the InfoIcon in a group for tooltip hover */}
                      <div className="relative group">
                        <InfoIcon className="w-4 h-4 text-slate-400 cursor-help" />
                        <div className="absolute left-0 sm:left-1/2 transform sm:transform-x-1/2 bottom-full mb-2 hidden group-hover:block bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-3 text-slate-300 w-52 sm:w-64 z-50">
                          Should this form show up on the Resident Dashboard
                          documents as a form that MUST be completed?
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 relative">
                      <input
                        type="checkbox"
                        id="isResidentDocumentCheckbox"
                        checked={isResidentDocument}
                        onChange={(e) =>
                          setIsResidentDocument(e.target.checked)
                        }
                        className="rounded text-blue-500 focus:ring-blue-500 h-4 w-4"
                      />
                      <label
                        htmlFor="isResidentDocumentCheckbox"
                        className="text-slate-300 text-sm"
                      >
                        Resident Documents
                      </label>

                      {/* Tooltip for explanation */}
                      <div className="relative group">
                        <InfoIcon className="w-4 h-4 text-slate-400 cursor-help" />
                        <div className="absolute left-0 sm:left-1/2 transform sm:transform-x-1/2 bottom-full mb-2 hidden group-hover:block bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-3 text-slate-300 w-52 sm:w-64 z-50">
                          Documents that residents can access at any time, such
                          as overnight request forms, maintenance requests,
                          behavioral contracts, etc.
                        </div>
                      </div>
                    </div>

                    {/* Add this back in if you add back the reoccuring forms from here. actively use the calendar for reoccurring forms */}
                    {/*
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isReoccurringCheckbox"
                checked={isReoccurringForm}
                onChange={(e) => setIsReoccurringForm(e.target.checked)}
                className="rounded text-blue-500 focus:ring-blue-500 h-4 w-4"
              />
              <label
                htmlFor="isReoccurringCheckbox"
                className="text-slate-300 text-sm"
              >
                Is this a recurring form?
              </label>
            </div>
              */}
                  </div>

                  {/* Right column - Reoccurrence settings this currently wont show since the check box is commented out. */}

                  {/*
{isReoccurringForm && (
<div className="space-y-4 bg-slate-800/20 p-4 rounded-lg">
<div>
<label className="block text-slate-300 text-sm mb-2">
Recurrence Type
</label>
<div className="flex gap-4">
<label className="flex items-center gap-2 text-slate-300 text-sm">
<input
  type="radio"
  name="recurrenceType"
  value="weekly"
  checked={reoccurrenceType === "weekly"}
  onChange={() => setReoccurrenceType("weekly")}
  className="text-blue-500 focus:ring-blue-500"
/>
Weekly
</label>
<label className="flex items-center gap-2 text-slate-300 text-sm">
<input
  type="radio"
  name="recurrenceType"
  value="monthly"
  checked={reoccurrenceType === "monthly"}
  onChange={() => setReoccurrenceType("monthly")}
  className="text-blue-500 focus:ring-blue-500"
/>
Monthly
</label>
</div>
</div>

{reoccurrenceType === "weekly" ? (
<div>
<label className="block text-slate-300 text-sm mb-2">
Day of Week
</label>
<select
value={reoccurrenceDay}
onChange={(e) => setReoccurrenceDay(e.target.value)}
className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
>
<option value="">Select day</option>
<option value="0">Sunday</option>
<option value="1">Monday</option>
<option value="2">Tuesday</option>
<option value="3">Wednesday</option>
<option value="4">Thursday</option>
<option value="5">Friday</option>
<option value="6">Saturday</option>
</select>
</div>
) : (
<div>
<label className="block text-slate-300 text-sm mb-2">
Day of Month
</label>
<select
value={reoccurrenceDay}
onChange={(e) => setReoccurrenceDay(e.target.value)}
className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
>
<option value="">Select day</option>
{Array.from({ length: 31 }, (_, i) => (
  <option key={i + 1} value={i + 1}>
    {i + 1}
  </option>
))}
<option value="last">Last day of month</option>
</select>
</div>
)}

<div>
<label className="block text-slate-300 text-sm mb-2">
Start Date (first occurrence)
</label>
<input
type="date"
value={reoccurrenceDate}
onChange={(e) => setReoccurrenceDate(e.target.value)}
className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white"
/>
</div>
</div>
)}
*/}
                </div>
              </div>
            )}

            {/* Form Actions */}
            {formElements.length > 0 && (
              <div className="mt-4 sm:mt-8 flex flex-col sm:flex-row justify-end gap-2 sm:gap-4 items-stretch sm:items-center">
                <button
                  className="px-4 py-2 bg-slate-800/50 text-slate-300 rounded-lg hover:bg-slate-800 transition-colors w-full sm:w-auto text-sm sm:text-base"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you want to clear this form? This action cannot be undone."
                      )
                    ) {
                      setFormElements([]);
                      setEditingForm(null);
                      setFormName("Untitled Form");
                      setIsApplication(false);
                      setShowDashboard(false);
                      setIsResidentDocument(false);
                    }
                  }}
                >
                  Clear Form
                </button>

                <button
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg transition-colors shadow-lg hover:shadow-blue-500/25 w-full sm:w-auto text-sm sm:text-base"
                  onClick={editingForm ? handleUpdateForm : handleSaveForm}
                  disabled={isSaving}
                >
                  {isSaving
                    ? "Saving..."
                    : editingForm
                    ? "Update Form"
                    : "Save Form"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {isManagingForms && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center p-4">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-xs sm:max-w-2xl">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-blue-500/10 rounded-lg">
                  <FileEdit className="text-blue-400 w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-white">
                  Manage Forms
                </h3>
              </div>
              <button
                onClick={() => setIsManagingForms(false)}
                className="p-1.5 sm:p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300 w-5 h-5" />
              </button>
            </div>

            <div className="p-3 sm:p-6 max-h-[60vh] overflow-y-auto">
              {existingForms.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  No forms found. Create your first form!
                </div>
              ) : (
                existingForms.map((form) => {
                  const shareUrl = `${window.location.origin}/public-forms/${organizationId}/${form.id}`;
                  const embedCode = generateEmbedCode(form);
                  const lastModifiedDate = new Date(
                    form.lastModified.seconds * 1000
                  ).toLocaleDateString();

                  return (
                    <div
                      key={form.id}
                      className="group w-full flex flex-col sm:flex-row sm:items-center p-3 sm:p-4 mb-3 sm:mb-4 bg-slate-800/50 hover:bg-slate-800 rounded-lg transition-colors"
                    >
                      {/* Left side: form name & date */}
                      <div className="flex-1 mb-2 sm:mb-0">
                        <h4 className="font-medium text-sm sm:text-base">
                          {form.name}
                        </h4>
                        <p className="text-xs sm:text-sm text-slate-400">
                          Last modified: {lastModifiedDate}
                        </p>
                      </div>

                      {/* Mobile visible action buttons */}
                      <div className="flex sm:hidden items-center justify-between border-t border-slate-700/50 pt-2">
                        <div className="flex space-x-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteForm(form.id);
                            }}
                            className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectForm(form);
                            }}
                            className="p-1.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20"
                          >
                            <FileEdit className="w-4 h-4 text-blue-400" />
                          </button>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(shareUrl);
                              alert(`Copied Link: ${shareUrl}`);
                            }}
                            className="p-1.5 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 text-xs"
                          >
                            URL
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(embedCode);
                              alert(`Copied Embed Code:\n${embedCode}`);
                            }}
                            className="p-1.5 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 text-xs"
                          >
                            Embed
                          </button>
                        </div>
                      </div>

                      {/* Desktop action buttons (hidden on mobile) */}
                      <div className="hidden sm:flex items-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteForm(form.id);
                          }}
                          className="p-2 ml-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition-all"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectForm(form);
                          }}
                          className="p-2 ml-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-blue-500/20 transition-all"
                        >
                          <FileEdit className="w-4 h-4 text-blue-400" />
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(shareUrl);
                            alert(`Copied Link: ${shareUrl}`);
                          }}
                          className="p-2 ml-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-slate-700/50 transition-all text-sm"
                        >
                          Get URL
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(embedCode);
                            alert(`Copied Embed Code:\n${embedCode}`);
                          }}
                          className="p-2 ml-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-slate-700/50 transition-all text-sm"
                        >
                          Get Embed
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      <MobileElementMenu
        elements={FORM_ELEMENTS}
        onAddElement={addFormElement}
      />
      <FloatingElementMenu
        elements={FORM_ELEMENTS}
        onAddElement={addFormElement}
      />
    </div>
  );
};

export default Forms;
